import { sectionRenderer, syncPriceRangeCheckboxes, getActivePriceRangeOption } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, formatMoney, startViewTransition } from '@theme/utilities';

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = null) {
    const newParameters = new URLSearchParams();
    const section = this.closest('.shopify-section') || document;

    let sourceForm = null;

    if (formData instanceof FormData) {
      for (const [key, value] of formData.entries()) {
        if (value !== '' && value !== null) {
          if (key === 'sort_by') {
            newParameters.set('sort_by', value.toString());
          } else {
            newParameters.append(key, value.toString());
          }
        }
      }
    } else {
      sourceForm = this.refs?.facetsForm || this.querySelector('form');
      const hasFilterInputs =
        sourceForm && sourceForm.querySelector('input[name^="filter."], input[name="price_range_option"]');

      if (sourceForm && hasFilterInputs) {
        // Active filter form (desktop sidebar, mobile drawer or lifestyle cards)
        const formFd = new FormData(sourceForm);
        for (const [key, value] of formFd.entries()) {
          if (value !== '' && value !== null) {
            if (key === 'sort_by') {
              newParameters.set('sort_by', value.toString());
            } else {
              newParameters.append(key, value.toString());
            }
          }
        }

        // The lifestyle cards and the sidebar/drawer are separate forms: keep the other form's selections
        const companionForm = this.#getCompanionFilterForm(sourceForm, section);
        if (companionForm instanceof HTMLFormElement) {
          const companionFd = new FormData(companionForm);
          for (const [key, value] of companionFd.entries()) {
            if (value !== '' && value !== null && key !== 'sort_by') {
              newParameters.append(key, value.toString());
            }
          }
        }
      } else {
        // Controls form (e.g. top sorting bar) - preserve existing active filters from current visible filter form or URL
        const isMobile = window.innerWidth < 750;
        const mainFilterForm = isMobile
          ? section.querySelector('.facets--drawer form')
          : section.querySelector('.facets--vertical form');

        if (mainFilterForm instanceof HTMLFormElement) {
          const formFd = new FormData(mainFilterForm);
          for (const [key, value] of formFd.entries()) {
            if (value !== '' && value !== null && key !== 'sort_by') {
              newParameters.append(key, value.toString());
            }
          }

          const lifestyleForm = section.querySelector('form[data-lifestyle-form]');
          if (lifestyleForm instanceof HTMLFormElement) {
            for (const [key, value] of new FormData(lifestyleForm).entries()) {
              if (value !== '' && value !== null) newParameters.append(key, value.toString());
            }
          }
        } else {
          // Fallback to URL search params for filters
          const currentUrlParams = new URLSearchParams(window.location.search);
          for (const [key, value] of currentUrlParams.entries()) {
            if (key.startsWith('filter.') || key === 'price_range_option') {
              newParameters.append(key, value);
            }
          }
        }

        // Add sorting from current controls form if available
        if (sourceForm instanceof HTMLFormElement) {
          const formFd = new FormData(sourceForm);
          const sortBy = formFd.get('sort_by');
          if (sortBy) {
            newParameters.set('sort_by', sortBy.toString());
          }
        }
      }
    }

    // Ensure sort_by is set if not already present
    if (!newParameters.has('sort_by')) {
      const activeSortRadio = section.querySelector('sorting-filter-component input[name="sort_by"]:checked');
      const activeSortSelect = section.querySelector('sorting-filter-component select[name="sort_by"]');
      if (activeSortRadio instanceof HTMLInputElement && activeSortRadio.value) {
        newParameters.set('sort_by', activeSortRadio.value);
      } else if (activeSortSelect instanceof HTMLSelectElement && activeSortSelect.value) {
        newParameters.set('sort_by', activeSortSelect.value);
      } else {
        const urlSortBy = new URL(window.location.href).searchParams.get('sort_by');
        if (urlSortBy) {
          newParameters.set('sort_by', urlSortBy);
        }
      }
    }

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    // Handle price range option checkboxes
    const priceOption = newParameters.get('price_range_option');
    if (priceOption) {
      if (priceOption === 'below_100') {
        newParameters.set('filter.v.price.lte', '100');
        newParameters.delete('filter.v.price.gte');
      } else if (priceOption === '101_150') {
        newParameters.set('filter.v.price.gte', '101');
        newParameters.set('filter.v.price.lte', '150');
      } else if (priceOption === 'above_150') {
        newParameters.set('filter.v.price.gte', '150');
        newParameters.delete('filter.v.price.lte');
      }
      newParameters.delete('price_range_option');
    }

    newParameters.delete('page');

    // Ensure availability filter has at most one value selected (mutual exclusivity)
    const availabilityValues = newParameters.getAll('filter.v.availability');
    if (availabilityValues.length > 1) {
      const lastVal = availabilityValues[availabilityValues.length - 1];
      newParameters.delete('filter.v.availability');
      newParameters.set('filter.v.availability', lastVal);
    }

    // Automatically enforce stock availability when any size filter is selected (if user hasn't explicitly selected availability)
    let hasSizeFilter = false;
    for (const [key, value] of newParameters.entries()) {
      const lowerKey = key.toLowerCase();
      if ((lowerKey.includes('size') || lowerKey.includes('shoe_size')) && value) {
        hasSizeFilter = true;
        break;
      }
    }
    if (hasSizeFilter && !newParameters.has('filter.v.availability')) {
      newParameters.set('filter.v.availability', '1');
    }

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Finds the filter form that sits next to the given one (lifestyle cards <-> sidebar/drawer)
   * @param {HTMLFormElement} sourceForm - The form the change came from
   * @param {ParentNode} section - The section containing both forms
   * @returns {HTMLFormElement | null} The companion form, if any
   */
  #getCompanionFilterForm(sourceForm, section) {
    const lifestyleForm = section.querySelector('form[data-lifestyle-form]');

    if (lifestyleForm instanceof HTMLFormElement && sourceForm !== lifestyleForm) {
      return lifestyleForm;
    }

    if (sourceForm === lifestyleForm) {
      const isMobile = window.innerWidth < 750;
      const mainFilterForm = isMobile
        ? section.querySelector('.facets--drawer form')
        : section.querySelector('.facets--vertical form');
      return mainFilterForm instanceof HTMLFormElement ? mainFilterForm : null;
    }

    return null;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = () => {
    this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  };

  /**
   * Updates the section
   */
  async #updateSection() {
    await sectionRenderer.renderSection(this.sectionId);
    this.updateFilterButtonInMobile();
    syncActiveFilterPills();
    syncCustomFilterCheckboxes();
    syncFilterCountBubble();
  }

  updateFilterButtonInMobile() {
    const fixedFilterButton = document.querySelector('sticky-facets-toggle');
    if (fixedFilterButton) {
      fixedFilterButton.init();
    }
    
    const drawerDialog = document.querySelector('#filters-drawer dialog');
    const isDrawerOpen = drawerDialog && (drawerDialog.open || drawerDialog.hasAttribute('open'));

    if (window.innerWidth <= 768 && !isDrawerOpen) {
      requestAnimationFrame(() => {
        const grid = document.querySelector('.product-grid-container');
        const OFFSET = 100;
  
        if (grid) {
          const y = grid.getBoundingClientRect().top + window.pageYOffset - OFFSET;
          window.scrollTo({
            top: y,
            behavior: 'smooth'
          });
        }
      });
    }

  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    const parsedUrl = new URL(url, window.location.origin);
    let hasSizeFilter = false;
    for (const [key, value] of parsedUrl.searchParams.entries()) {
      const lowerKey = key.toLowerCase();
      if ((lowerKey.includes('size') || lowerKey.includes('shoe_size')) && value) {
        hasSizeFilter = true;
        break;
      }
    }
    if (hasSizeFilter && !parsedUrl.searchParams.has('filter.v.availability')) {
      parsedUrl.searchParams.set('filter.v.availability', '1');
    }
    history.pushState('', '', parsedUrl.toString());
    this.dispatchEvent(new FilterUpdateEvent(parsedUrl.searchParams));
    this.#updateSection();
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   * @param {Event} [event] - The change event
   */
  updateFilters(event) {
    if (event?.target instanceof HTMLInputElement && event.target.name === 'price_range_option' && event.target.checked) {
      const allPriceCheckboxes = this.closest('form')?.querySelectorAll('input[name="price_range_option"]');
      allPriceCheckboxes?.forEach((cb) => {
        if (cb !== event.target && cb instanceof HTMLInputElement) cb.checked = false;
      });
    }

    if (
      event?.target instanceof HTMLInputElement &&
      (event.target.name === 'filter.v.availability' || event.target.name.includes('availability'))
    ) {
      const form = this.closest('form');
      const allAvailabilityCheckboxes = form?.querySelectorAll(`input[name="${event.target.name}"]`);
      allAvailabilityCheckboxes?.forEach((cb) => {
        if (cb !== event.target && cb instanceof HTMLInputElement) {
          if (event.target.checked) {
            cb.checked = false;
            cb.disabled = true;
          } else {
            cb.disabled = false;
          }
        }
      });
    }

    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const value = Number(input.value);
    const min = Number(formatMoney(input.getAttribute('data-min') ?? ''));
    const max = Number(formatMoney(input.getAttribute('data-max') ?? ''));

    if (value < min) input.value = min.toString();
    if (value > max) input.value = max.toString();
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }
    if (event) {
      event.preventDefault();
    }

    const isClearAll =
      this.classList.contains('facets__clear-all') ||
      this.dataset.isClearAll === 'true' ||
      !!this.querySelector('.facets__clear-all') ||
      (event?.target instanceof HTMLElement && !!event.target.closest('.facets__clear-all'));

    const currentUrl = new URL(window.location.href);

    if (isClearAll) {
      // Clear all filters: preserve path and sort_by, remove filter.* and price_range_option
      for (const [key] of Array.from(currentUrl.searchParams.entries())) {
        if (key.startsWith('filter.') || key === 'price_range_option') {
          currentUrl.searchParams.delete(key);
        }
      }
      const targetUrl = currentUrl.toString();
      const facetsForm = document.querySelector('facets-form-component');
      if (facetsForm && typeof facetsForm.updateFiltersByURL === 'function') {
        facetsForm.updateFiltersByURL(targetUrl);
      } else {
        FacetRemoveComponent.#navigateWithLoadingState(targetUrl);
      }
      return;
    }

    const filterName = this.dataset.filterName;
    const filterValue = this.dataset.filterValue;
    const isPrice = this.dataset.filterIsPrice === 'true';

    if (isPrice) {
      const minName = this.dataset.filterMinName || 'filter.v.price.gte';
      const maxName = this.dataset.filterMaxName || 'filter.v.price.lte';
      currentUrl.searchParams.delete(minName);
      currentUrl.searchParams.delete(maxName);
      currentUrl.searchParams.delete('price_range_option');
    } else if (filterName && filterValue !== undefined) {
      // Remove ONLY this specific filter value from the URL
      const allValues = currentUrl.searchParams.getAll(filterName);
      currentUrl.searchParams.delete(filterName);
      allValues.filter((v) => v !== filterValue).forEach((v) => currentUrl.searchParams.append(filterName, v));
    } else if (this.dataset.url) {
      try {
        const parsed = new URL(this.dataset.url, window.location.origin);
        const targetUrl = parsed.toString();
        const facetsForm = document.querySelector('facets-form-component');
        if (facetsForm && typeof facetsForm.updateFiltersByURL === 'function') {
          facetsForm.updateFiltersByURL(targetUrl);
        } else {
          FacetRemoveComponent.#navigateWithLoadingState(targetUrl);
        }
        return;
      } catch (e) {}
    } else {
      // Fallback: match by pill label text
      const pillText = this.textContent.trim().replace(/\u2715|\u00d7/g, '').trim().toLowerCase();
      for (const [key, value] of currentUrl.searchParams.entries()) {
        if (key.startsWith('filter.')) {
          const cleanVal = value.toLowerCase().replace(/^eu\s*/i, '');
          if (pillText && (pillText === cleanVal || pillText.includes(cleanVal) || cleanVal.includes(pillText))) {
            const allValues = currentUrl.searchParams.getAll(key);
            currentUrl.searchParams.delete(key);
            allValues.filter((v) => v !== value).forEach((v) => currentUrl.searchParams.append(key, v));
            break;
          }
        }
      }
    }

    const targetUrl = currentUrl.toString();
    const facetsForm = document.querySelector('facets-form-component');
    if (facetsForm && typeof facetsForm.updateFiltersByURL === 'function') {
      facetsForm.updateFiltersByURL(targetUrl);
    } else {
      FacetRemoveComponent.#navigateWithLoadingState(targetUrl);
    }
  }

  /**
   * Fades the product grid and active pills before navigating to avoid a jarring blank-screen flash.
   * @param {string} url
   */
  static #navigateWithLoadingState(url) {
    const grid = document.querySelector('.product-grid-container');
    if (grid) {
      grid.style.transition = 'opacity 0.15s ease';
      grid.style.opacity = '0.4';
    }
    window.location.assign(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const targetVal = event?.target?.value;
    if (targetVal) {
      // Sync across all sorting radio buttons and selects in this section
      const section = this.closest('.shopify-section') || document;
      section.querySelectorAll('sorting-filter-component').forEach((sortingComp) => {
        sortingComp.querySelectorAll('input[type="radio"][name="sort_by"]').forEach((radio) => {
          if (radio instanceof HTMLInputElement) {
            radio.checked = radio.value === targetVal;
            const optionWrap = radio.closest('.sorting-filter__option');
            if (optionWrap) {
              optionWrap.setAttribute('aria-selected', radio.checked ? 'true' : 'false');
            }
          }
        });
        sortingComp.querySelectorAll('select[name="sort_by"]').forEach((select) => {
          if (select instanceof HTMLSelectElement) {
            select.value = targetVal;
          }
        });
      });
    }

    this.updateFacetStatus(event);
    facetsForm.updateFilters();

    // Close the details element when a value is selected
    const details = this.querySelector('details');
    if (details instanceof HTMLDetailsElement) {
      details.open = false;
    }
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    const target = event?.target;
    if (!target) return;

    const details = this.querySelector('details');
    if (!details) return;

    const statusEl = details.querySelector('.facets__status') || details.querySelector('facet-status-component');
    const optionName = target.dataset?.optionName || (target.options ? target.options[target.selectedIndex]?.text : null);

    if (statusEl && optionName) {
      statusEl.textContent = optionName.trim();
    }
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    const filterStyle = this.dataset.filterStyle;

    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (filterStyle === 'horizontal' && checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const minInputNum = this.#parseCents(minInputValue, '0');
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as cents
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid
   * @returns {number} The money value in cents
   */
  #parseCents(value, fallback = '0') {
    const parts = value ? value.trim().split(/[^0-9]/) : (parseInt(fallback, 10) / 100).toString();
    const [wholeStr, fractionStr, ...rest] = parts;
    if (typeof wholeStr !== 'string' || rest.length > 0) return parseInt(fallback, 10);

    const whole = parseInt(wholeStr, 10);
    let fraction = parseInt(fractionStr || '0', 10);

    // Use two most-significant digits, e.g. 1 -> 10, 12 -> 12, 123 -> 12.3, 1234 -> 12.34, etc
    fraction = fraction * Math.pow(10, 2 - fraction.toString().length);

    return whole * 100 + fraction;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const template = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount') {
        // Check first since it's the most common, use defaults.
      } else if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        // Weirdly, this is correct. It uses amount_with_comma_separator's
        // behaviour but removes decimals, resulting in an unintuitive
        // output that can't possibly include commas, despite the name.
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * Formats money in cents
   * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [a, b] = roundedNumber.split('.');
    if (!a) a = '0';
    if (!b) b = '';

    // Split by groups of 3 digits
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}

/**
 * Default currency decimals used in most currenies
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};

export function syncActiveFilterPills() {
  const removeContainers = document.querySelectorAll('.facets-remove');
  if (!removeContainers.length) return;

  const currentUrl = new URL(window.location.href);
  const activeParams = [];

  let hasAnyActiveFilter = false;
  for (const [key, value] of currentUrl.searchParams.entries()) {
    if ((key.startsWith('filter.') || key === 'price_range_option') && value) {
      hasAnyActiveFilter = true;
      if (key !== 'filter.v.price.gte' && key !== 'filter.v.price.lte') {
        activeParams.push({ key, value });
      }
    }
  }

  removeContainers.forEach((container) => {
    const existingPills = Array.from(container.querySelectorAll('.facets-remove__pill'));
    const existingTexts = existingPills.map((pill) => pill.textContent.trim().toLowerCase());

    activeParams.forEach(({ key, value }) => {
      // Don't create raw gid:// taxonomy pills
      if (value.startsWith('gid://')) return;

      let label = value;
      let matchingInput = null;
      try {
        matchingInput = document.querySelector(`input[name="${CSS.escape(key)}"][value="${CSS.escape(value)}"]`);
      } catch (e) {
        // Fallback if key/val contains special characters
      }

      if (matchingInput) {
        label = matchingInput.dataset?.label || matchingInput.getAttribute('aria-label') || matchingInput.closest('label')?.textContent?.trim() || value;
      }

      if (label.startsWith('gid://')) return;

      if (key.toLowerCase().includes('availability')) {
        label = value === '1' ? 'In Stock' : (value === '0' ? 'Out of Stock' : value);
      } else if (key.toLowerCase().includes('size')) {
        const cleanSize = value.replace(/^EU\s*/i, '').trim();
        label = `EU (${cleanSize})`;
      }

      const labelLower = label.toLowerCase();
      // Check if Liquid already rendered a pill that handles this parameter
      const alreadyRendered = existingPills.some((pill) => {
        if (pill.dataset?.url) {
          try {
            const pillUrl = new URL(pill.dataset.url, window.location.origin);
            const remainingVals = pillUrl.searchParams.getAll(key);
            if (!remainingVals.includes(value)) return true;
          } catch (e) {}
        }
        const text = pill.textContent.trim().toLowerCase();
        return text.includes(labelLower) || labelLower.includes(text);
      });

      if (!alreadyRendered) {
        const removeUrl = new URL(window.location.href);
        const allVals = removeUrl.searchParams.getAll(key);
        removeUrl.searchParams.delete(key);
        allVals.filter((v) => v !== value).forEach((v) => removeUrl.searchParams.append(key, v));

        const pill = document.createElement('facet-remove-component');
        pill.className = 'pills__pill pills__pill--desktop-small facets-remove__pill';
        pill.dataset.url = removeUrl.toString();
        pill.dataset.filterName = key;
        pill.dataset.filterValue = value;
        pill.tabIndex = 0;
        pill.setAttribute('role', 'button');
        pill.setAttribute('on:click', '/removeFilter?form=');
        pill.setAttribute('on:keydown', '/removeFilter?form=');
        pill.innerHTML = `
          <span>${label}</span>
          <span class="svg-wrapper svg-wrapper--smaller">
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1L9 9M9 1L1 9" stroke="rgba(0, 0, 0, 0.45)" stroke-width="1.2" stroke-linecap="round"/>
            </svg>
          </span>
          <span class="visually-hidden">Remove</span>
        `;
        pill.addEventListener('click', (e) => {
          e.preventDefault();
          const facetsForm = document.querySelector('facets-form-component');
          if (facetsForm && typeof facetsForm.updateFiltersByURL === 'function') {
            facetsForm.updateFiltersByURL(removeUrl.toString());
          } else {
            window.location.href = removeUrl.toString();
          }
        });
        const clearComponent = container.querySelector('facet-remove-component:has(.facets__clear-all), facet-remove-component[data-is-clear-all="true"]');
        if (clearComponent) {
          container.insertBefore(pill, clearComponent);
        } else {
          container.appendChild(pill);
        }
      }
    });

    // Price pill for the fallback price filter (the native price filter renders its own pill in Liquid)
    const activePriceOption = getActivePriceRangeOption(currentUrl.searchParams);
    if (activePriceOption && !container.querySelector('[data-filter-is-price]')) {
      const priceInput = document.querySelector(
        `input[name="price_range_option"][value="${activePriceOption}"]`
      );
      const priceLabel = priceInput?.closest('.facets__checkbox-custom-wrap')?.querySelector('.facets__checkbox-custom-text');
      const removeUrl = new URL(window.location.href);
      removeUrl.searchParams.delete('filter.v.price.gte');
      removeUrl.searchParams.delete('filter.v.price.lte');

      const pill = document.createElement('facet-remove-component');
      pill.className = 'pills__pill pills__pill--desktop-small facets-remove__pill';
      pill.dataset.url = removeUrl.toString();
      pill.dataset.filterIsPrice = 'true';
      pill.tabIndex = 0;
      pill.setAttribute('role', 'button');
      pill.innerHTML = `
        <span></span>
        <span class="svg-wrapper svg-wrapper--smaller">
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1L9 9M9 1L1 9" stroke="rgba(0, 0, 0, 0.45)" stroke-width="1.2" stroke-linecap="round"/>
          </svg>
        </span>
        <span class="visually-hidden">Remove</span>
      `;
      pill.querySelector('span').textContent = priceLabel?.textContent?.trim() || activePriceOption;
      pill.addEventListener('click', (e) => {
        e.preventDefault();
        const facetsForm = document.querySelector('facets-form-component');
        if (facetsForm && typeof facetsForm.updateFiltersByURL === 'function') {
          facetsForm.updateFiltersByURL(removeUrl.toString());
        } else {
          window.location.href = removeUrl.toString();
        }
      });
      const clearComponent = container.querySelector('facet-remove-component:has(.facets__clear-all), facet-remove-component[data-is-clear-all="true"]');
      if (clearComponent) {
        container.insertBefore(pill, clearComponent);
      } else {
        container.appendChild(pill);
      }
    }

    if (hasAnyActiveFilter || activeParams.length > 0) {
      let clearAllEl = container.querySelector('.facets__clear-all');
      if (!clearAllEl) {
        const clearUrl = new URL(window.location.href);
        for (const [key] of currentUrl.searchParams.entries()) {
          if (key.startsWith('filter.') || key === 'price_range_option') {
            clearUrl.searchParams.delete(key);
          }
        }
        const clearComponent = document.createElement('facet-remove-component');
        clearComponent.dataset.url = clearUrl.toString();
        clearComponent.dataset.isClearAll = 'true';
        clearComponent.tabIndex = 0;
        clearComponent.setAttribute('role', 'button');
        clearComponent.setAttribute('on:click', '/removeFilter?form=');
        clearComponent.setAttribute('on:keydown', '/removeFilter?form=');
        clearComponent.innerHTML = `
          <button type="button" class="button-unstyled facets__clear-all-link facets__clear-all active" on:click="/removeFilter?form=" on:keydown="/removeFilter?form=">
            Clear all
          </button>
        `;
        clearComponent.addEventListener('click', (e) => {
          e.preventDefault();
          const facetsForm = document.querySelector('facets-form-component');
          if (facetsForm && typeof facetsForm.updateFiltersByURL === 'function') {
            facetsForm.updateFiltersByURL(clearUrl.toString());
          } else {
            window.location.href = clearUrl.toString();
          }
        });
        container.appendChild(clearComponent);
      } else {
        const parentComponent = clearAllEl.closest('facet-remove-component');
        if (parentComponent) container.appendChild(parentComponent);
      }
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    syncActiveFilterPills();
    syncCustomFilterCheckboxes();
    syncFilterCountBubble();
  });
} else {
  syncActiveFilterPills();
  syncCustomFilterCheckboxes();
  syncFilterCountBubble();
}

/**
 * Synchronizes the filter count bubble number on mobile toggle button
 */
export function syncFilterCountBubble() {
  const currentParams = new URLSearchParams(window.location.search);
  let filterCount = 0;
  let hasPriceFilter = false;
  for (const [key, value] of currentParams.entries()) {
    if (!value) continue;
    if (key === 'filter.v.price.gte' || key === 'filter.v.price.lte' || key === 'price_range_option') {
      if (!hasPriceFilter) {
        hasPriceFilter = true;
        filterCount++;
      }
    } else if (key.startsWith('filter.')) {
      filterCount++;
    }
  }

  const toggleButtons = document.querySelectorAll('.facets-toggle__button, .sticky-facets-toggle__button');
  toggleButtons.forEach((btn) => {
    let bubble = btn.querySelector('.filter-count-bubble');
    if (filterCount > 0) {
      if (!bubble) {
        bubble = document.createElement('div');
        bubble.id = 'filter-count-bubble-toggle';
        bubble.className = 'filter-count-bubble';
        bubble.innerHTML = `
          <span class="filter-count-bubble__background"></span>
          <span class="filter-count-bubble__text" aria-hidden="true">${filterCount}</span>
        `;
        btn.appendChild(bubble);
      } else {
        const textEl = bubble.querySelector('.filter-count-bubble__text');
        if (textEl) textEl.textContent = filterCount.toString();
        bubble.style.display = '';
      }
    } else if (bubble) {
      bubble.remove();
    }
  });
}

/**
 * Restores checked state on custom (hardcoded) filter checkboxes
 * that Shopify doesn't auto-mark as checked (e.g. the Style filter circles).
 * Reads all filter params from window.location.search and sets .checked accordingly.
 */
export function syncCustomFilterCheckboxes() {
  const currentParams = new URLSearchParams(window.location.search);

  syncPriceRangeCheckboxes(document, currentParams);

  document.querySelectorAll('input[type="checkbox"][name^="filter."]').forEach((input) => {
    if (!(input instanceof HTMLInputElement)) return;
    const { name, value } = input;
    if (!name || !value) return;

    const activeValues = currentParams.getAll(name);
    let shouldBeChecked = activeValues.includes(value);

    if (!shouldBeChecked && name.includes('filter_style')) {
      if (
        (value === 'Flats' || value === 'Ballet Flats') &&
        (activeValues.includes('Flats') || activeValues.includes('Ballet Flats'))
      ) {
        shouldBeChecked = true;
      }
    }

    if (input.checked !== shouldBeChecked) {
      input.checked = shouldBeChecked;
    }

    const wrapper = input.closest('.facets__style-card-wrapper');
    if (wrapper) {
      const label = wrapper.querySelector('.facets__style-card-label');
      if (label) {
        label.classList.toggle('is-active', shouldBeChecked);
      }
    }
  });
}
